import { rem } from "polished";
import type { HTMLAttributes, ReactNode } from "react";

import { Icon, IconProps } from "../../atoms/Icon";
import { sizes } from "../../design-tokens/dimensions";
import {
  Actions,
  CardHeading,
  CardTag,
  ContentWrapper,
  ImageWrapper,
  Note,
  Overlay,
  PriceLabel,
  ProductContent,
  ProductDetails,
  ProductFooter,
  Root,
} from "./styles";

export type ProductCardVariant = "compact" | "expanded";

export type ProductCardProps = {
  /**
   * `ReactNode` to display background image behind `logoImage`.
   */
  backgroundImage: ReactNode;
  /**
   * Body text.
   */
  body: string;
  /**
   * Button for the card.
   */
  button: ReactNode;
  /**
   * Formatted Price.
   */
  formattedPrice?: string;
  /**
   * Heading for the card.
   */
  heading: string;
  /**
   * Product ownership state to determine if price and button should be shown.
   */
  isOwned?: boolean;
  /**
   * `ReactNode` to display product logo or overlay asset.
   */
  logoImage?: ReactNode;
  /**
   * Relevant `Tags` to show for the product or offering.
   */
  tags: string[];
  /**
   * Style variation of the product card.
   */
  variant?: ProductCardVariant;
  /**
   * Note config object - controls its content, icon and colour.
   */
  note?: {
    content: ReactNode;
    color?: string;
    icon?: IconProps["icon"];
  };
} & HTMLAttributes<HTMLDivElement>;

/**
 *  Variation of a `Card` that is aimed at promoting products and encouraging users to purchase them.
 */
export function ProductCard({
  backgroundImage,
  body,
  button,
  formattedPrice,
  heading,
  isOwned = false,
  logoImage,
  tags,
  variant = "expanded",
  note,
  ...rest
}: ProductCardProps) {
  return (
    <Root data-testid={ProductCard.name} {...rest}>
      <ImageWrapper variant={variant}>
        {backgroundImage}
        {logoImage ? <Overlay>{logoImage}</Overlay> : null}
      </ImageWrapper>
      <ContentWrapper variant={variant}>
        <ProductDetails variant={variant}>
          <CardHeading level="h3">{heading}</CardHeading>
          {tags.length > 0 && (
            <div>
              {tags.flatMap((tag, index) => [
                index > 0 && (
                  <span key={`divider-${index}`} aria-hidden="true">
                    +
                  </span>
                ),
                <CardTag key={`${tag}-${index}`}>{tag}</CardTag>,
              ])}
            </div>
          )}
          <ProductContent lines={3}>{body}</ProductContent>
        </ProductDetails>
        <ProductFooter variant={variant}>
          {note && (
            <Note color={note.color}>
              {note.icon && (
                <Icon
                  icon={note.icon}
                  color={note.color}
                  height={sizes.s16.rem}
                  style={{ marginTop: rem(-2) }}
                />
              )}
              {note.content}
            </Note>
          )}
          {!isOwned && (
            <Actions data-testid={`${ProductCard.name}-actions`}>
              {button}
              {formattedPrice && (
                <PriceLabel data-testid={`${ProductCard.name}-price`}>{formattedPrice}</PriceLabel>
              )}
            </Actions>
          )}
        </ProductFooter>
      </ContentWrapper>
    </Root>
  );
}
